import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { getLlegadas, modificarFechaLlegada } from '../../services/motosSnopServices';

const LlegadasModal = ({ open, onClose, modeloPlaneacion, tipoPeriodo = 'mes', escenario = 'inicial_2025_6', onDataUpdate, readOnly = false }) => {
  const [llegadas, setLlegadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (open && modeloPlaneacion) {
      const fetchLlegadas = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await getLlegadas(modeloPlaneacion, escenario, tipoPeriodo);
          if (response && response.documentos) {
            setLlegadas(response.documentos);
          } else {
            setLlegadas([]);
          }
        } catch (err) {
          console.error('Error al cargar llegadas:', err);
          setError('Error al cargar las llegadas');
          setLlegadas([]);
        } finally {
          setLoading(false);
        }
      };
      fetchLlegadas();
    }
  }, [open, modeloPlaneacion, tipoPeriodo]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Crear la fecha como fecha local para evitar problemas de zona horaria
    const parts = dateString.split('T')[0].split('-'); // Tomar solo la parte de fecha (YYYY-MM-DD)
    const date = new Date(parts[0], parts[1] - 1, parts[2]); // año, mes (0-indexado), día
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Crear la fecha como fecha local para evitar problemas de zona horaria
    const parts = dateString.split('T')[0].split('-'); // Tomar solo la parte de fecha (YYYY-MM-DD)
    const date = new Date(parts[0], parts[1] - 1, parts[2]); // año, mes (0-indexado), día
    return date.toISOString().split('T')[0];
  };

  const isDateModified = (fechaOriginal, fechaModificada) => fechaOriginal !== fechaModificada && fechaModificada;

  const handleEditClick = (documento) => {
    // En escenarios actualizados (solo lectura) el modal es solo de consulta.
    if (readOnly) return;
    const rowKey = `${documento.documento_compra}-${documento.posicion}-${documento.fecha_documento}`;
    setEditingRow(rowKey);
    setEditingDate(formatDateForInput(documento.fecha_modificada || documento.fecha_disponible_para_facturacion));
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditingDate('');
  };

  const handleSaveDate = async (documento) => {
    if (!editingDate) return;
    
    setUpdating(true);
    try {
      const fechaData = {
        modelo_planeacion: modeloPlaneacion,
        documento_compra: documento.documento_compra,
        posicion: documento.posicion,
        fecha: editingDate,
        tipo_periodo: tipoPeriodo,
        escenario
      };

      const response = await modificarFechaLlegada(fechaData);
      
      if (response) {
        // Actualizar el estado local
        setLlegadas(prevLlegadas => 
          prevLlegadas.map(doc => 
            doc.documento_compra === documento.documento_compra && 
            doc.posicion === documento.posicion &&
            doc.fecha_documento === documento.fecha_documento
              ? { ...doc, fecha_modificada: editingDate }
              : doc
          )
        );
        setEditingRow(null);
        setEditingDate('');
        
        // Notificar al componente padre que se actualizaron los datos
        if (onDataUpdate) {
          onDataUpdate();
        }
      } else {
        setError('Error al actualizar la fecha');
        // Cancelar la edición para mostrar la fecha original
        handleCancelEdit();
      }
    } catch (err) {
      console.error('Error al actualizar fecha:', err);
      setError('Error al actualizar la fecha');
      // Cancelar la edición para mostrar la fecha original
      handleCancelEdit();
    } finally {
      setUpdating(false);
    }
  };

  // Obtener la fecha actual en formato YYYY-MM-DD para usar como fecha mínima
  const getCurrentDate = () => new Date().toISOString().split('T')[0];

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="llegadas-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 1200,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f5f5f5'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography id="llegadas-modal-title" variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Llegadas - {modeloPlaneacion}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'grey.500',
              '&:hover': { color: 'grey.700', bgcolor: 'grey.100' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : llegadas.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <LocalShippingIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay llegadas registradas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No se encontraron documentos de llegada para este modelo.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Fecha Documento
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Documento de Compras
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Posición
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Cantidad de Pedido
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Cantidad Contenedor
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Fecha Llegada
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Fecha Llegada Auteco (Modificada)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Comentario
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {llegadas.map((documento) => {
                    const rowKey = `${documento.documento_compra}-${documento.posicion}-${documento.fecha_documento}`;
                    const isEditing = editingRow === rowKey;
                    
                    return (
                      <TableRow 
                        key={rowKey}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                          '&:hover': { bgcolor: '#f0f0f0' }
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {formatDate(documento.fecha_documento)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {documento.documento_compra || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {documento.posicion || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {documento.cantidad_pedido || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {documento.cantidad_de_contenedores || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {formatDate(documento.fecha_disponible_para_facturacion)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {isEditing ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                type="date"
                                value={editingDate}
                                onChange={(e) => setEditingDate(e.target.value)}
                                size="small"
                                sx={{ minWidth: 140 }}
                                disabled={updating}
                                inputProps={{
                                  min: getCurrentDate() // No permitir fechas anteriores a hoy
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleSaveDate(documento)}
                                disabled={updating || !editingDate}
                                sx={{ color: 'green' }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                disabled={updating}
                                sx={{ color: 'red' }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isDateModified(
                                documento.fecha_disponible_para_facturacion,
                                documento.fecha_modificada
                              ) ? (
                                <Chip
                                  label={formatDate(documento.fecha_modificada)}
                                  color="error"
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontSize: '0.75rem', cursor: readOnly ? 'default' : 'pointer' }}
                                  onClick={readOnly ? undefined : () => handleEditClick(documento)}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: readOnly ? 'default' : 'pointer',
                                    '&:hover': { bgcolor: readOnly ? 'transparent' : 'rgba(0,0,0,0.04)' },
                                    p: 0.5,
                                    borderRadius: 1
                                  }}
                                  onClick={readOnly ? undefined : () => handleEditClick(documento)}
                                >
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {formatDate(documento.fecha_disponible_para_facturacion)}
                                  </Typography>
                                  {!readOnly && (
                                    <EditIcon sx={{ fontSize: 14, ml: 0.5, color: 'grey.500' }} />
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {documento.comentario || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default LlegadasModal; 